/** 通话背景音音量范围。界面和保存都用 0.1–10，步进 0.1，默认 0.3。 */
export const BACKGROUND_SOUND_VOLUME_MIN = 0.1
export const BACKGROUND_SOUND_VOLUME_MAX = 10
export const DEFAULT_BACKGROUND_SOUND_VOLUME = 0.3

/**
 * 接口未接上前的兜底列表。
 * value 用于保存和运行时，label 只用于展示。
 */
const BUILTIN_BACKGROUND_SOUND_OPTIONS = [
  { value: 'office', label: '办公室', audio_file_path: '' },
]

const DEFAULT_BACKGROUND_SOUND_ID = 'office'
const DEFAULT_BACKGROUND_SOUND_NAME = '办公室'

function readText(value) {
  return value != null ? String(value).trim() : ''
}

export function clampBackgroundSoundVolume(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return DEFAULT_BACKGROUND_SOUND_VOLUME
  const clamped = Math.min(
    BACKGROUND_SOUND_VOLUME_MAX,
    Math.max(BACKGROUND_SOUND_VOLUME_MIN, n),
  )
  return Math.round(clamped * 10) / 10
}

/**
 * 统一成 { value, label, audio_file_path }。
 * 兼容内置字段，以及接口可能返回的 id / name / url。
 */
export function normalizeBackgroundSoundOption(raw) {
  if (!raw || typeof raw !== 'object') return null
  const value = readText(
    raw.value != null ? raw.value : raw.id != null ? raw.id : raw.sound_id,
  )
  const label =
    readText(
      raw.label != null ? raw.label : raw.name != null ? raw.name : raw.sound_name,
    ) || value
  if (!value) return null
  const audio = readText(
    raw.audio_file_path != null
      ? raw.audio_file_path
      : raw.audioFilePath != null
        ? raw.audioFilePath
        : raw.url != null
          ? raw.url
          : raw.file_path,
  )
  return {
    value,
    label,
    audio_file_path: audio,
  }
}

export function normalizeBackgroundSoundOptions(list) {
  const source = Array.isArray(list) ? list : []
  const seen = new Set()
  const result = []
  source.forEach((item) => {
    const option = normalizeBackgroundSoundOption(item)
    if (!option || seen.has(option.value)) return
    seen.add(option.value)
    result.push(option)
  })
  return result
}

export function getBuiltinBackgroundSoundOptions() {
  return normalizeBackgroundSoundOptions(BUILTIN_BACKGROUND_SOUND_OPTIONS)
}

export function createEmptyBackgroundSound(volume = DEFAULT_BACKGROUND_SOUND_VOLUME) {
  return {
    sound_id: '',
    sound_name: '',
    audio_file_path: '',
    volume: clampBackgroundSoundVolume(volume),
  }
}

export function createDefaultBackgroundSound() {
  const builtin = getBuiltinBackgroundSoundOptions().find(
    (item) => item.value === DEFAULT_BACKGROUND_SOUND_ID,
  )
  return {
    sound_id: DEFAULT_BACKGROUND_SOUND_ID,
    sound_name: builtin ? builtin.label : DEFAULT_BACKGROUND_SOUND_NAME,
    audio_file_path: builtin ? builtin.audio_file_path : '',
    volume: DEFAULT_BACKGROUND_SOUND_VOLUME,
  }
}

export function normalizeBackgroundSound(raw) {
  const defaults = createDefaultBackgroundSound()
  if (raw == null || typeof raw !== 'object') return defaults
  const soundId = readText(
    raw.sound_id != null ? raw.sound_id : raw.soundId != null ? raw.soundId : raw.id,
  )
  if (!soundId) {
    return createEmptyBackgroundSound(raw.volume != null ? raw.volume : defaults.volume)
  }
  const soundName =
    readText(
      raw.sound_name != null ? raw.sound_name : raw.soundName != null ? raw.soundName : raw.name,
    ) || (soundId === defaults.sound_id ? defaults.sound_name : soundId)
  const audio = readText(
    raw.audio_file_path != null
      ? raw.audio_file_path
      : raw.audioFilePath != null
        ? raw.audioFilePath
        : raw.url,
  )
  return {
    sound_id: soundId,
    sound_name: soundName,
    audio_file_path: audio,
    volume: clampBackgroundSoundVolume(raw.volume != null ? raw.volume : defaults.volume),
  }
}

/**
 * 拉取通话背景音选项。
 * 目前返回内置列表。接口就绪后只改这里，表单仍消费 { value, label, audio_file_path }。
 * 期望 data：[{ id|value, name|label, url|audio_file_path }]。失败时回退内置列表。
 */
export async function fetchBackgroundSoundOptions() {
  return getBuiltinBackgroundSoundOptions()
}
